import { Link } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  Store, ShoppingBag, ClipboardList, Wallet, Boxes, PackagePlus, MapPinned, ClipboardCheck, Package, DollarSign, Ruler,
  ShoppingCart, Truck, FilePlus, Users,
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { useModuleLicense } from '../../hooks/useModuleLicense';

/**
 * /retail — a retail nursery's shop, in one place (retail plan, 2026-10-12).
 *
 * The shop works the same stock items, lots, places, orders and tills a
 * growing nursery does, so each tile opens that page; a Retail seat reaches
 * it (useStockSeat). Staff work the counter and the stock; the Retail
 * Manager also keeps the catalogue and the prices.
 */

type Tile = { label: string; description: string; icon: LucideIcon; path: string };

const COUNTER: Tile[] = [
  { label: 'New sale', icon: ShoppingBag, path: '/nursery/orders/new',
    description: 'Scan or type what the customer is taking, then take payment' },
  { label: 'Orders', icon: ClipboardList, path: '/nursery/orders',
    description: 'Sales and orders, paid and waiting' },
  { label: 'Tills', icon: Wallet, path: '/nursery/tills',
    description: 'Open a till with a float, cash in and out, close with a count' },
  { label: 'Customers', icon: Users, path: '/accounts/customers',
    description: 'What a customer owes, and cash for their invoices into a till' },
];

const STOCK: Tile[] = [
  { label: 'Stock on hand', icon: Boxes, path: '/nursery/lots',
    description: 'Every plant and product on the shop floor and in the storeroom, with what it cost' },
  { label: 'Put stock out', icon: PackagePlus, path: '/nursery/lots/new',
    description: 'Record stock that has come in, where it is and what one cost' },
  { label: 'Shop places', icon: MapPinned, path: '/nursery/locations',
    description: 'The shop floor, sales tables and storerooms stock is kept in' },
  { label: 'Stocktake', icon: ClipboardCheck, path: '/nursery/stocktake',
    description: 'Count what is there; the count updates stock on hand' },
];

// Buying in (retail plan Phase 1): staff receive deliveries; the manager orders.
const DELIVERIES: Tile = { label: 'Purchase orders', icon: ShoppingCart, path: '/purchase-orders',
  description: 'What has been ordered, and receiving a delivery into stock at what it cost' };
const BUYING: Tile[] = [
  { label: 'Order stock', icon: FilePlus, path: '/purchase-orders/new?kind=nursery',
    description: 'A purchase order to a supplier for plants and merchandise' },
  DELIVERIES,
  { label: 'Suppliers', icon: Truck, path: '/accounts/suppliers',
    description: 'Who you buy from, and where their orders are emailed' },
];

const CATALOGUE: Tile[] = [
  { label: 'Stock items', icon: Package, path: '/nursery/stock-items',
    description: 'Plants from the cultivar registry, merchandise, shop categories and barcodes' },
  { label: 'Price lists', icon: DollarSign, path: '/accounts/price-lists',
    description: 'What the shop charges, and the default list every sale is priced from' },
  { label: 'Pot sizes', icon: Ruler, path: '/nursery/tree-types',
    description: 'The sizes and forms plants are sold in (140 mm, 20 cm, bare root)' },
];

function Section({ title, note, tiles }: { title: string; note: string; tiles: Tile[] }) {
  return (
    <section>
      <h2 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">{title}</h2>
      <p className="text-sm text-neutral-500 mt-1 mb-3 break-words">{note}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {tiles.map(({ label, description, icon: Icon, path }) => (
          <Link
            key={path}
            to={path}
            className="flex items-start gap-3 p-4 bg-white border border-neutral-200 rounded-xl hover:border-primary-300 hover:shadow-sm transition-all"
          >
            <Icon className="h-6 w-6 text-primary-600 shrink-0 mt-0.5" />
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-neutral-800 break-words">{label}</span>
              <span className="block text-sm text-neutral-500 break-words">{description}</span>
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}

function RetailDashboard() {
  const { hasAccess: isManager, loading } = useModuleLicense('retail', 'manager');

  return (
    <>
      <PageHeader
        title="Retail"
        subtitle="The shop: counter, stock on hand, tills and prices"
        icon={<Store className="h-6 w-6 text-white" />}
      />
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-8">
        <Section title="Counter" note="Sales across the counter, and the cash in the tills." tiles={COUNTER} />
        <Section title="Stock" note="What the shop holds, where, and what it cost." tiles={STOCK} />
        {!loading && (
          <Section
            title="Buying"
            note={isManager
              ? 'Order from suppliers; deliveries go into stock at their landed cost.'
              : 'Receive a delivery into stock against its purchase order.'}
            tiles={isManager ? BUYING : [DELIVERIES]}
          />
        )}
        {!loading && isManager && (
          <Section title="Catalogue and prices" note="What the shop sells and what it charges. Retail Manager." tiles={CATALOGUE} />
        )}
      </div>
    </>
  );
}

export default RetailDashboard;
